import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { repAPI } from '../../services/api';

const LEAD_STATUSES = ['NEW', 'CONTACTED', 'QUALIFIED', 'PROPOSAL_SENT', 'NEGOTIATING', 'CLOSED_WON', 'CLOSED_LOST', 'PENDING_ADMIN_APPROVAL'];
const LEAD_SOURCES = ['COLD_CALL', 'REFERRAL', 'WEBSITE', 'TRADE_SHOW', 'EMAIL_CAMPAIGN', 'SOCIAL_MEDIA', 'OTHER', 'REP_CREATED'];

const LeadEditDialog = ({ open, onClose, lead, onSuccess }) => {
  const [formData, setFormData] = useState({
    clinicName: '',
    contactName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    source: 'OTHER',
    status: 'NEW',
    priority: 3,
    estimatedAnnualVolume: '',
    numberOfProviders: '',
    currentSupplier: '',
    interestedProducts: '',
    expectedCloseDate: '',
    estimatedValue: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (lead && open) {
      setFormData({
        clinicName: lead.clinicName || '',
        contactName: lead.contactName || '',
        email: lead.email || '',
        phone: lead.phone || '',
        address: lead.address || '',
        city: lead.city || '',
        state: lead.state || '',
        zipCode: lead.zipCode || '',
        source: lead.source || 'OTHER',
        status: lead.status || 'NEW',
        priority: lead.priority || 3,
        estimatedAnnualVolume: lead.estimatedAnnualVolume?.toString() || '',
        numberOfProviders: lead.numberOfProviders?.toString() || '',
        currentSupplier: lead.currentSupplier || '',
        interestedProducts: lead.interestedProducts || '',
        expectedCloseDate: lead.expectedCloseDate ? new Date(lead.expectedCloseDate).toISOString().split('T')[0] : '',
        estimatedValue: lead.estimatedValue ? parseFloat(lead.estimatedValue).toString() : '',
      });
      setError('');
    }
  }, [lead, open]);

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.clinicName || !formData.clinicName.trim()) {
      setError('Clinic name is required');
      return;
    }
    if (!formData.contactName || !formData.contactName.trim()) {
      setError('Contact name is required');
      return;
    }
    if (!formData.email || !formData.email.trim()) {
      setError('Email is required');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const updateData = {
        clinicName: formData.clinicName.trim(),
        contactName: formData.contactName.trim(),
        email: formData.email.trim(),
        phone: formData.phone?.trim() || null,
        address: formData.address?.trim() || null,
        city: formData.city?.trim() || null,
        state: formData.state?.trim() || null,
        zipCode: formData.zipCode?.trim() || null,
        source: formData.source,
        status: formData.status,
        priority: parseInt(formData.priority),
        estimatedAnnualVolume: formData.estimatedAnnualVolume ? parseInt(formData.estimatedAnnualVolume) : null,
        numberOfProviders: formData.numberOfProviders ? parseInt(formData.numberOfProviders) : null,
        currentSupplier: formData.currentSupplier?.trim() || null,
        interestedProducts: formData.interestedProducts?.trim() || null,
        expectedCloseDate: formData.expectedCloseDate ? new Date(formData.expectedCloseDate) : null,
        estimatedValue: formData.estimatedValue ? parseFloat(formData.estimatedValue) : null,
      };

      const response = await repAPI.updateLead(lead.id, updateData);
      
      if (response.success) {
        onSuccess();
      } else {
        setError(response.error || 'Failed to update lead');
      }
    } catch (err) {
      console.error('Error updating lead:', err);
      setError(err.message || 'Failed to update lead. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Lead</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
          <TextField 
            label="Clinic Name *" 
            value={formData.clinicName} 
            onChange={(e) => setFormData({ ...formData, clinicName: e.target.value })}
            disabled={loading}
            required
          />
          <TextField 
            label="Contact Name *" 
            value={formData.contactName} 
            onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
            disabled={loading}
            required
          />
          <TextField 
            label="Email *" 
            type="email" 
            value={formData.email} 
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            disabled={loading}
            required
          />
          <TextField 
            label="Phone" 
            value={formData.phone} 
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            disabled={loading}
          />
          <TextField 
            label="Address" 
            value={formData.address} 
            onChange={(e) => setFormData({ ...formData, address: e.target.value })} 
            fullWidth 
            sx={{ gridColumn: '1 / -1' }}
            disabled={loading}
          />
          <TextField 
            label="City" 
            value={formData.city} 
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            disabled={loading}
          />
          <TextField 
            label="State" 
            value={formData.state} 
            onChange={(e) => setFormData({ ...formData, state: e.target.value })}
            disabled={loading}
          />
          <TextField 
            label="Zip Code" 
            value={formData.zipCode} 
            onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
            disabled={loading}
          />
          <TextField 
            select 
            label="Source" 
            value={formData.source} 
            onChange={(e) => setFormData({ ...formData, source: e.target.value })}
            disabled={loading}
          >
            {LEAD_SOURCES.map((source) => (
              <MenuItem key={source} value={source}>
                {source.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ')}
              </MenuItem>
            ))}
          </TextField>
          <TextField 
            select 
            label="Status" 
            value={formData.status} 
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            disabled={loading}
          >
            {LEAD_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {status.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ')}
              </MenuItem>
            ))}
          </TextField>
          <TextField 
            select 
            label="Priority" 
            value={formData.priority} 
            onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
            disabled={loading}
          >
            <MenuItem value={1}>High</MenuItem>
            <MenuItem value={2}>Medium</MenuItem>
            <MenuItem value={3}>Low</MenuItem>
          </TextField>
          <TextField 
            label="Estimated Annual Volume" 
            type="number"
            value={formData.estimatedAnnualVolume} 
            onChange={(e) => setFormData({ ...formData, estimatedAnnualVolume: e.target.value })}
            disabled={loading}
          />
          <TextField 
            label="Number of Providers" 
            type="number"
            value={formData.numberOfProviders} 
            onChange={(e) => setFormData({ ...formData, numberOfProviders: e.target.value })}
            disabled={loading}
          />
          <TextField 
            label="Current Supplier" 
            value={formData.currentSupplier} 
            onChange={(e) => setFormData({ ...formData, currentSupplier: e.target.value })}
            fullWidth 
            sx={{ gridColumn: '1 / -1' }}
            disabled={loading}
          />
          <TextField 
            label="Interested Products" 
            value={formData.interestedProducts} 
            onChange={(e) => setFormData({ ...formData, interestedProducts: e.target.value })}
            fullWidth 
            sx={{ gridColumn: '1 / -1' }}
            multiline
            rows={2}
            disabled={loading}
          />
          <TextField 
            label="Expected Close Date" 
            type="date"
            value={formData.expectedCloseDate} 
            onChange={(e) => setFormData({ ...formData, expectedCloseDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            disabled={loading}
          />
          <TextField 
            label="Estimated Value" 
            type="number"
            value={formData.estimatedValue} 
            onChange={(e) => setFormData({ ...formData, estimatedValue: e.target.value })}
            disabled={loading}
            inputProps={{ step: 0.01 }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || !formData.clinicName || !formData.contactName || !formData.email}
        >
          {loading ? <CircularProgress size={20} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeadEditDialog;

